import type { Extension, Observation } from '@medplum/fhirtypes';

/**
 * Frontend mirror of the clinical-findings review markers the extraction
 * agent stamps on every AI-proposed finding — see
 * `libs/backend/mini-apps/src/lib/agents/clinical-findings/systems.ts` and
 * `finding-type.ts` (the backend source of truth for these semantics).
 * The systems are environment-independent constants rooted at the
 * centralized Eluve FHIR namespace (ENG-4575), so mirroring the strings is
 * safe; the backend lib itself is not importable from frontend code.
 */
const ELUVE_FHIR_NAMESPACE = 'https://eluve.com';

/** meta.tag system carrying the human-review state of an AI finding. */
export const REVIEW_STATUS_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/review-status`;
export const REVIEW_STATUS_PENDING = 'pending';
/** Human approved the finding → its native status is final/amended. */
export const REVIEW_STATUS_ATTESTED = 'attested';
/** Human rejected the finding → its native status is entered-in-error. */
export const REVIEW_STATUS_REJECTED = 'rejected';

/** Extension carrying the agent's 0–1 confidence in an extracted finding. */
export const FINDING_CONFIDENCE_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/finding-confidence`;

/**
 * Still awaiting human review: the uniform `review-status: pending` tag, or —
 * for observations written outside the agent pipeline — a bare `preliminary`
 * status (ticket scope treats both as unverified). Retracted resources are
 * never pending, even when a stale pending tag lingers after a supersede.
 */
export function isPendingReview(observation: Observation): boolean {
  if (observation.status === 'entered-in-error') return false;
  if (observation.status === 'preliminary') return true;
  return Boolean(
    observation.meta?.tag?.some(
      (tag) =>
        tag.system === REVIEW_STATUS_SYSTEM &&
        tag.code === REVIEW_STATUS_PENDING,
    ),
  );
}

/** Retracted (dismissed / superseded) — off the chart everywhere. */
export function isRetracted(observation: Observation): boolean {
  return observation.status === 'entered-in-error';
}

/** The supporting transcript passage stored on a finding (first note.text). */
export function readEvidence(observation: Observation): string | null {
  return observation.note?.[0]?.text ?? null;
}

/** The 0–1 confidence stamped on a finding's extension, or null. */
export function readConfidence(observation: Observation): number | null {
  const extension = (
    observation as { extension?: Extension[] }
  ).extension?.find((e) => e.url === FINDING_CONFIDENCE_EXTENSION);
  return extension?.valueDecimal ?? null;
}

/**
 * Replace the `review-status` meta.tag (pending → attested / rejected),
 * preserving every other tag — same transition the backend reviewer applies.
 */
export function setReviewStatusTag(
  observation: Observation,
  code: string,
): Observation {
  const others = (observation.meta?.tag ?? []).filter(
    (tag) => tag.system !== REVIEW_STATUS_SYSTEM,
  );
  return {
    ...observation,
    meta: {
      ...observation.meta,
      tag: [...others, { system: REVIEW_STATUS_SYSTEM, code }],
    },
  };
}
